import { inject, Injectable } from '@angular/core'
import { LanguagesTaiga } from '@modules/lang-module/constants/languages-taiga'
import { LanguageEnum } from '@modules/lang-module/enums/language.enum'
import { TranslateService } from '@ngx-translate/core'
import { TUI_ENGLISH_LANGUAGE, TuiLanguage } from '@taiga-ui/i18n'
import { BehaviorSubject } from 'rxjs'
import { LocalesInterface } from '@modules/lang-module/interfaces/locales.interface'
import { KeyLanguageLocalStorageConstant } from '@modules/lang-module/constants/key-language-local-storage.constant'
import { UserHelperService } from '@modules/user-module/services/user-helper.service'

@Injectable({
	providedIn: 'root',
})
export class LanguageService {
	protected readonly json: JSON = JSON
	protected readonly object: ObjectConstructor = Object
	protected readonly languagesTaiga: Record<LanguageEnum, TuiLanguage> = LanguagesTaiga
	protected readonly languageEnum: typeof LanguageEnum = LanguageEnum
	protected readonly keyLanguageLocalStorageConstant: string = KeyLanguageLocalStorageConstant

	public language$: BehaviorSubject<LanguageEnum> = new BehaviorSubject<LanguageEnum>(this.languageEnum.ENGLISH)
	public languageTaiga$: BehaviorSubject<TuiLanguage> = new BehaviorSubject<TuiLanguage>(TUI_ENGLISH_LANGUAGE)

	private readonly _translateService: TranslateService = inject<TranslateService>(TranslateService)
	private readonly _userHelperService: UserHelperService = inject<UserHelperService>(UserHelperService)

	private _localesDictionary: LocalesInterface[] = []
	private _isInitedLanguage: boolean = false

	get languageFromLocalStorage(): LanguageEnum | null {
		return this.json.parse(localStorage.getItem(this.keyLanguageLocalStorageConstant) ?? 'null')
	}

	private _applyLocale(locales: LocalesInterface): void {
		for (const language of this.object.values(this.languageEnum)) {
			this._translateService.setTranslation(language, locales.locales[language as LanguageEnum], true)
		}
	}

	private _updateLocale(): void {
		for (const language of this.object.values(this.languageEnum)) {
			this._translateService.resetLang(language)
		}

		this._localesDictionary.forEach((locales: LocalesInterface): void => this._applyLocale(locales))
	}

	private _getSystemLanguage(): LanguageEnum {
		const isSystemLangRu: boolean = navigator.language?.toLowerCase().startsWith('ru')

		return isSystemLangRu ? this.languageEnum.RUSSIAN : this.languageEnum.ENGLISH
	}

	public initLanguage(): void {
		const languageFromWebSettings: LanguageEnum | null | undefined =
			this._userHelperService.userDetail$.getValue()?.web_settings?.lang
		const languageFromLocalStorage: LanguageEnum | null = this.languageFromLocalStorage

		this.changeLanguage(languageFromWebSettings ?? languageFromLocalStorage ?? this._getSystemLanguage())
		this._isInitedLanguage = true
	}

	public useLocale(locales: LocalesInterface): void {
		this._applyLocale(locales)

		this._localesDictionary = [...this._localesDictionary, locales]
	}

	public deleteLocale(key: string): void {
		this._localesDictionary = this._localesDictionary.filter(
			(locale: LocalesInterface): boolean => locale.key !== key,
		)

		this._updateLocale()
	}

	public saveLanguage(language: LanguageEnum): void {
		localStorage.setItem(this.keyLanguageLocalStorageConstant, this.json.stringify(language))
	}

	public changeLanguage(language: LanguageEnum): void {
		if (this._isInitedLanguage && this.language$.getValue() === language) return
		this.saveLanguage(language)
		this.language$.next(language)
		this.languageTaiga$.next(this.languagesTaiga[language])
		this._translateService.use(language)
	}
}
